import { useCallback, useState } from 'react';
import { type ApiError, getStats } from '../api/client';
import { useDebounced, usePolling } from '../api/hooks';
import { ChartsPanel } from '../components/charts';
import { MetricCard } from '../components/MetricCard';
import { EmptyState, ErrorBanner, ErrorState, LoadingState } from '../components/StateViews';
import { formatDuration, formatInteger, formatPercent } from '../lib/format';
import type { FailureKind, Stats } from '../types';

const POLL_INTERVAL_MS = 10_000;

const FAILURE_KIND_ORDER: FailureKind[] = ['http_status', 'timeout', 'connection_error', 'unknown'];

const FAILURE_KIND_LABELS: Record<FailureKind, string> = {
  http_status: 'HTTP status',
  timeout: 'Timeout',
  connection_error: 'Connection error',
  unknown: 'Unknown',
};

function deltaLabel(firstAttempt: number, eventual: number): string {
  const points = (eventual - firstAttempt) * 100;
  if (!Number.isFinite(points)) return '—';
  return `${points >= 0 ? '+' : ''}${points.toFixed(1)} pts`;
}

function RetryDistribution({ stats }: { stats: Stats }) {
  const buckets = Array.isArray(stats.retryDistribution) ? stats.retryDistribution : [];

  if (buckets.length === 0) {
    return <p className="muted">No successful deliveries yet, so there is nothing to distribute.</p>;
  }

  const maxShare = buckets.reduce(
    (max, bucket) => (Number.isFinite(bucket.share) && bucket.share > max ? bucket.share : max),
    0,
  );

  return (
    <ul className="distribution">
      {buckets.map((bucket) => (
        <li key={bucket.attemptNumber} className="distribution__row">
          <span className="distribution__label">Attempt {bucket.attemptNumber}</span>
          <span className="distribution__track" aria-hidden="true">
            <span
              className="distribution__fill"
              style={{ width: `${maxShare > 0 ? Math.max(2, (bucket.share / maxShare) * 100) : 0}%` }}
            />
          </span>
          <span className="distribution__value mono">{formatPercent(bucket.share)}</span>
          <span className="distribution__count muted">{formatInteger(bucket.succeeded)}</span>
        </li>
      ))}
    </ul>
  );
}

function FailureKinds({ stats }: { stats: Stats }) {
  const kinds = stats.failureKinds;
  const total = FAILURE_KIND_ORDER.reduce((sum, kind) => sum + (kinds?.[kind] ?? 0), 0);

  if (total === 0) {
    return <p className="muted">No failed attempts recorded.</p>;
  }

  return (
    <ul className="kinds">
      {FAILURE_KIND_ORDER.map((kind) => {
        const count = kinds?.[kind] ?? 0;
        return (
          <li key={kind} className={`kinds__row ${count === 0 ? 'kinds__row--zero' : ''}`}>
            <span className="kinds__label">{FAILURE_KIND_LABELS[kind]}</span>
            <span className="kinds__value mono">{formatInteger(count)}</span>
            <span className="kinds__share muted">{formatPercent(count / total)}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function StatsView() {
  const [benchmarkRunId, setBenchmarkRunId] = useState('');
  const debouncedRunId = useDebounced(benchmarkRunId, 400);

  const fetcher = useCallback(
    (signal: AbortSignal) => getStats({ benchmarkRunId: debouncedRunId || null }, signal),
    [debouncedRunId],
  );

  const { data, error, loading, refresh } = usePolling(fetcher, POLL_INTERVAL_MS);

  const filterField = (
    <label className="toolbar__field">
      <span>Benchmark run</span>
      <input
        className="input input--sm mono"
        value={benchmarkRunId}
        onChange={(changeEvent) => setBenchmarkRunId(changeEvent.target.value)}
        placeholder="all runs"
        autoComplete="off"
        aria-label="Filter statistics by benchmark run ID"
      />
    </label>
  );

  return (
    <div className="view">
      <header className="view__head">
        <div>
          <p className="view__kicker">Aggregates</p>
          <h1 className="view__title">Stats</h1>
          <p className="view__lede">
            First-attempt vs eventual success, latency, retry distribution, and dead-letter rate.
          </p>
        </div>
        <div className="toolbar__group">
          {filterField}
          <button type="button" className="btn btn--ghost btn--sm" onClick={refresh}>
            Refresh
          </button>
        </div>
      </header>

      {error !== null && data === null ? (
        <ErrorState error={error} onRetry={refresh} context="the statistics" />
      ) : loading && data === null ? (
        <LoadingState label="Loading statistics" rows={4} />
      ) : data === null ? null : (
        <StatsBody stats={data} error={error} onRetry={refresh} filtered={debouncedRunId !== ''} />
      )}
    </div>
  );
}

interface StatsBodyProps {
  stats: Stats;
  error: ApiError | null;
  onRetry: () => void;
  filtered: boolean;
}

function StatsBody({ stats, error, onRetry, filtered }: StatsBodyProps) {
  const totals = stats.totals;
  const successRate = stats.successRate;
  const latency = stats.latencyMs;

  if ((totals?.deliveries ?? 0) === 0) {
    return (
      <>
        {error !== null ? <ErrorBanner error={error} onRetry={onRetry} /> : null}
        <EmptyState title={filtered ? 'No data for this benchmark run' : 'No deliveries recorded yet'}>
          {filtered
            ? 'Nothing has been ingested under that benchmark run ID. Clear the filter to see everything.'
            : 'Send an event from the live feed and its delivery attempts will start showing up here.'}
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {error !== null ? <ErrorBanner error={error} onRetry={onRetry} /> : null}

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Does retrying work?</h2>
          <p className="panel__lede">
            Share of deliveries that landed, counting only the first attempt versus counting every
            retry.
          </p>
        </div>

        <div className="hero-metrics">
          <MetricCard
            label="First attempt"
            value={formatPercent(successRate?.firstAttempt)}
            hint="Delivered without any retry"
            tone="warning"
            size="hero"
          />
          <div className="hero-metrics__arrow" aria-hidden="true">
            →
          </div>
          <MetricCard
            label="Eventual"
            value={formatPercent(successRate?.eventual)}
            hint="Delivered within the retry budget"
            tone="success"
            size="hero"
          />
          <MetricCard
            label="Improvement"
            value={deltaLabel(successRate?.firstAttempt ?? 0, successRate?.eventual ?? 0)}
            hint="Percentage points recovered by retrying"
            tone="accent"
            size="hero"
          />
        </div>
      </section>

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Headline metrics</h2>
          <p className="panel__lede">
            Latency is measured end to end — ingestion through to successful delivery, including
            queue wait and every backoff delay.
          </p>
        </div>

        <div className="metric-grid">
          <MetricCard label="Dead-letter rate" value={formatPercent(stats.deadLetterRate)} hint="Gave up after the retry budget" tone="danger" />
          <MetricCard label="P50 latency" value={formatDuration(latency?.p50)} hint="Median" />
          <MetricCard label="P95 latency" value={formatDuration(latency?.p95)} hint="Tail" />
          <MetricCard label="P99 latency" value={formatDuration(latency?.p99)} hint="Worst case" />
          <MetricCard label="Mean latency" value={formatDuration(latency?.mean)} hint="Skewed by retries" />
        </div>
      </section>

      <div className="split">
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Retry distribution</h2>
            <p className="panel__lede">Which attempt finally succeeded, as a share of all successes.</p>
          </div>
          <RetryDistribution stats={stats} />
        </section>

        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Why attempts failed</h2>
            <p className="panel__lede">Classified failures across every attempt.</p>
          </div>
          <FailureKinds stats={stats} />
        </section>
      </div>

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Volume</h2>
        </div>
        <div className="metric-grid">
          <MetricCard label="Events" value={formatInteger(totals?.events)} />
          <MetricCard label="Deliveries" value={formatInteger(totals?.deliveries)} />
          <MetricCard label="Attempts" value={formatInteger(totals?.attempts)} />
          <MetricCard
            label="Attempts succeeded"
            value={formatInteger(stats.attemptOutcomes?.succeeded)}
            tone="success"
          />
          <MetricCard
            label="Attempts failed"
            value={formatInteger(stats.attemptOutcomes?.failed)}
            tone="danger"
          />
        </div>
      </section>

      {/* Optional: delete this line and `components/charts/` to drop Recharts. */}
      <ChartsPanel stats={stats} />
    </>
  );
}
